import { useQuery } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { ReferralDetail, OriginalSource, type Detail } from '@/features/agent-proposals/ReferralDetail';

export function InlineReferralReview({ org, jobId, onBack, wide = false, onOpenCurrentSource }: { org: string; jobId: string; onBack?: () => void; wide?: boolean; onOpenCurrentSource?: () => void }) {
  const base = `/api/v1/organizations/${org}/agent`;
  const detail = useQuery({
    queryKey: ['agent-detail', org, jobId],
    gcTime: 0,
    // Do not replace an actively edited intake form during background polling.
    refetchOnWindowFocus: false,
    refetchInterval: (query) => ['queued', 'dispatching', 'running'].includes(query.state.data?.status ?? '') ? 5000 : false,
    queryFn: async () => (await clientApi.get<Detail>(`${base}/proposals/${jobId}`)).data,
  });
  return (
    <section className="flex flex-col gap-4" aria-label="Assistant intake review">
      {onBack && (
        <Button variant="ghost" onClick={onBack}>
          Back to incoming work
        </Button>
      )}
      {detail.isError ? (
        <div className="flex flex-col items-start gap-3">
          <p role="alert">This earlier intake is unavailable or no longer matches the current document. It cannot be approved. Open the current document to review its contents and classification; do not keep repeating the preparation scan.</p>
          {onOpenCurrentSource && <Button variant="outline" onClick={onOpenCurrentSource}>Review current document and classification</Button>}
          <p>If the current document is accessible but intake remains unavailable, its source association needs investigation before a fresh intake can be prepared.</p>
        </div>
      ) : detail.data ? (
        <>
          <div className={wide ? 'grid items-start gap-4 xl:grid-cols-2' : 'flex flex-col gap-4'}>
          <OriginalSource base={base} jobId={jobId} />
          <ReferralDetail
            key={`${jobId}:${detail.data.provisional?.version || 0}`}
            detail={detail.data}
            processingEnabled={!!detail.data.processing_enabled}
          />
          </div>
        </>
      ) : (
        <p role="status">Loading intake review…</p>
      )}
    </section>
  );
}
